// React
import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

// Components
import Select, { DropdownIndicator, ClearIndicator } from 'ui/components/Select'
import { Option, Label, Remove } from './Option'

const createOption = (label) => ({
  value: label.trim(),
  label: label.trim()
})

const MultiSelectInput = ({
  field,
  delimiter = ',',
  useOptions = false,
  creatable = true,
  valueAsArray = false,
  ...otherProps
}) => {
  const [value, setValue] = useState(() => {
    if (!field.value) return []

    return Array.isArray(field.value) ? field.value.map(createOption) : field.value.split(delimiter).map(createOption)
  })
  const [inputValue, setInputValue] = useState('')

  const selectComponents = useMemo(
    () => ({
      DropdownIndicator: useOptions ? DropdownIndicator : null,
      ClearIndicator,
      MultiValueContainer: (props) => <Option {...props} validateEmail={otherProps.validateEmail} />,
      MultiValueLabel: Label,
      MultiValueRemove: Remove
    }),
    [otherProps.options, otherProps.validateEmail, useOptions]
  )

  const handleKeyDown = useCallback(
    (event) => {
      if (!inputValue) return

      // If the user presses enter, comma, space or tab create an option
      if ([' ', 'Enter', 'Tab', ','].includes(event.key)) {
        event.preventDefault()

        if (field.value) {
          field.onChange(`${field.value},${inputValue}`)
        } else {
          field.onChange(inputValue)
        }

        setValue((prev) => [...prev, createOption(inputValue)])
        setInputValue('')
      }
    },
    [field, inputValue]
  )

  const handleChange = useCallback((val) => {
    const newValue = val?.map(createOption) || []

    if (valueAsArray) {
      field.onChange(newValue?.map((option) => option.value) || [])
    } else {
      field.onChange(newValue?.map((option) => option.value).join(delimiter) || '')
    }

    setValue(newValue)
  }, [valueAsArray, delimiter, field])

  return (
    <Select
      {...otherProps}
      components={selectComponents}
      inputValue={inputValue}
      onKeyDown={creatable ? handleKeyDown : undefined}
      field={field}
      value={value}
      onChange={handleChange}
      onInputChange={setInputValue}
      {...(!useOptions && { menuIsOpen: false })}
      name={field.name}
      form={field.name}
      styles={{
        valueContainer: (base) => ({
          ...base,
          gap: 6
        })
      }}
      forceOnChange
      forceValue
      isMulti
      isClearable
    />
  )
}

MultiSelectInput.propTypes = {
  field: PropTypes.object,
  delimiter: PropTypes.string,
  useOptions: PropTypes.bool,
  creatable: PropTypes.bool,
  valueAsArray: PropTypes.bool
}

export default MultiSelectInput
